import { isLoggedIn } from '@/services/auth';
import { logger } from '@/utils/screenEventsLogger';
import { sendScreenDowntime } from '@/api/downtime';
import { LAST_CHECKED_AT, SCREEN_DOWNTIME_RECORDS } from '@/constants';
import { DOWNTIME_GAP_THRESHOLD_MS } from '@/config/constants';
import { ScreenDowntimeRecord } from '@/types/api/downtime';

function getErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'NA';
}

function getPendingRecords(): ScreenDowntimeRecord[] {
  try {
    const raw = localStorage.getItem(SCREEN_DOWNTIME_RECORDS);
    return raw ? JSON.parse(raw) : [];
  } catch (error) {
    logger.addLog(`ERROR: screenDowntime getPendingRecords - Message: ${getErrorMessage(error)}`);
    return [];
  }
}

function savePendingRecords(records: ScreenDowntimeRecord[]): void {
  localStorage.setItem(SCREEN_DOWNTIME_RECORDS, JSON.stringify(records));
}

// Compares 'now' against the last confirmed checkpoint. A gap larger than the
// allowed threshold means the checks stopped running (device off, crashed,
// screen locked, OS asleep, etc.) for that duration, regardless of the cause.
function detectGapAndUpdateCheckpoint(): void {
  const now = Date.now();
  const lastCheckedAtRaw = localStorage.getItem(LAST_CHECKED_AT);

  if (!lastCheckedAtRaw) {
    // First check ever (fresh install/login) - nothing to compare against yet.
    localStorage.setItem(LAST_CHECKED_AT, String(now));
    return;
  }

  const lastCheckedAt = Number(lastCheckedAtRaw);
  const gap = now - lastCheckedAt;

  if (gap < 0) {
    // Device clock moved backward (NTP correction, manual change, timezone fix).
    // We cannot infer a real downtime duration from this, so just resync.
    logger.addLog('Downtime check: clock moved backward, resetting checkpoint');
    localStorage.setItem(LAST_CHECKED_AT, String(now));
    return;
  }

  if (gap > DOWNTIME_GAP_THRESHOLD_MS) {
    const record: ScreenDowntimeRecord = {
      start_time: new Date(lastCheckedAt).toISOString(),
      end_time: new Date(now).toISOString(),
    };
    const pendingRecords = getPendingRecords();
    pendingRecords.push(record);
    savePendingRecords(pendingRecords);
    logger.addLog(`Downtime detected: ${record.start_time} to ${record.end_time}`);
  }

  localStorage.setItem(LAST_CHECKED_AT, String(now));
}

// Attempts to sync every pending record. Records that fail to send (offline,
// BE error) are kept for the next attempt; records that succeed are dropped.
async function flushPendingRecords(): Promise<void> {
  const pendingRecords = getPendingRecords();
  if (pendingRecords.length === 0) return;

  const remainingRecords: ScreenDowntimeRecord[] = [];
  for (const record of pendingRecords) {
    try {
      await sendScreenDowntime(record);
    } catch (error) {
      remainingRecords.push(record);
    }
  }

  savePendingRecords(remainingRecords);
}

export async function recordDowntime(): Promise<void> {
  // Downtime while logged out is attributed on the BE via connection_last_updated,
  // not tracked locally - and last_checked_at/pending records are wiped on
  // logout anyway, so the checkpoint naturally restarts clean on next login.
  if (!isLoggedIn()) return;

  try {
    detectGapAndUpdateCheckpoint();
    await flushPendingRecords();
  } catch (error) {
    logger.addLog(`ERROR: recordDowntime - Message: ${getErrorMessage(error)}`);
  }
}
